import { ChevronRight } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem
} from "@/components/ui/sidebar";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { portalHint, type PortalState } from "../hooks/usePortalState";
import {
  NAV_FOOTER_ITEMS,
  NAV_GROUPS,
  isProfileSubmenuTab,
  type NavBadgeKey,
  type NavItem
} from "../navConfig";
import type { ClientTab } from "../urlState";

const badgeLabel = (count: number) => (count > 99 ? "99+" : String(count));

/** Attention (due reminders, requests to review) in danger; plain totals muted. */
const CountBadge: FC<{ count: number; total?: number; className?: string }> = ({
  count,
  total,
  className
}) =>
  count > 0 ? (
    <SidebarMenuBadge
      className={cn(
        "rounded-full bg-danger px-1.5 text-xs font-medium text-white",
        className
      )}
    >
      {badgeLabel(count)}
    </SidebarMenuBadge>
  ) : total ? (
    <SidebarMenuBadge className={className}>
      {badgeLabel(total)}
    </SidebarMenuBadge>
  ) : null;

/** Steps aside for the row's "+" while the row is hovered or focused. */
const QUICK_ACTION_BADGE =
  "group-focus-within/menu-item:opacity-0 group-hover/menu-item:opacity-0";

export type ClientNavIdentity = {
  name: string;
  initials: string;
  avatarUrl?: string | null;
  email?: string | null;
  /** The newest case's visa type. */
  visa?: string | null;
  portal?: PortalState;
};

export type ClientNavProps = {
  activeTab: ClientTab;
  onSelect: (tab: ClientTab) => void;
  badgeCounts: Record<NavBadgeKey, number>;
  /** Muted totals beside a row (cases, requests), shown when nothing needs attention. */
  totals?: Partial<Record<ClientTab, number>>;
  isProfileExpanded: boolean;
  onProfileExpandedChange: (expanded: boolean) => void;
  /** Who this is: the identity block atop the nav. */
  identity?: ClientNavIdentity;
  /** The Requests explorer, nested under the Requests row while it is expanded. */
  requestsTree?: ReactNode;
  /** A "+" on the Requests row while it is collapsed (New Request). */
  requestsQuickAction?: ReactNode;
  isRequestsExpanded?: boolean;
  onRequestsExpandedChange?: (expanded: boolean) => void;
  className?: string;
};

const NavButton: FC<{
  item: NavItem;
  active: boolean;
  count: number;
  total?: number;
  onSelect: (tab: ClientTab) => void;
}> = ({ item, active, count, total, onSelect }) => {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        aria-current={active ? "page" : undefined}
        onClick={() => onSelect(item.id)}
      >
        <Icon />
        <span>{item.label}</span>
      </SidebarMenuButton>
      <CountBadge count={count} total={total} />
    </SidebarMenuItem>
  );
};

/**
 * The Requests row: selects the tab on the unfiltered list and unfolds the
 * stages beneath it; the chevron (shown only while open) folds them away
 * again, leaving the count.
 */
const RequestsNavButton: FC<{
  item: NavItem;
  active: boolean;
  count: number;
  total?: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onSelect: (tab: ClientTab) => void;
  tree: ReactNode;
  quickAction?: ReactNode;
}> = ({
  item,
  active,
  count,
  total,
  expanded,
  onExpandedChange,
  onSelect,
  tree,
  quickAction
}) => {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        aria-current={active ? "page" : undefined}
        aria-expanded={expanded}
        onClick={() => {
          onSelect(item.id);
          onExpandedChange(true);
        }}
        className="pr-8"
      >
        <Icon />
        <span>{item.label}</span>
      </SidebarMenuButton>
      {expanded ? (
        <>
          <SidebarMenuAction
            aria-label="Collapse Intake Requests"
            onClick={() => onExpandedChange(false)}
          >
            <ChevronRight className="rotate-90 transition-transform duration-200" />
          </SidebarMenuAction>
          <div className="pt-0.5 pb-1">{tree}</div>
        </>
      ) : (
        <>
          {quickAction}
          <CountBadge
            count={count}
            total={total}
            className={quickAction ? QUICK_ACTION_BADGE : undefined}
          />
        </>
      )}
    </SidebarMenuItem>
  );
};

/** Avatar, name and portal email, then the newest case's visa and the portal state. */
const IdentityHeader: FC<{ identity: ClientNavIdentity }> = ({ identity }) => {
  const email = identity.email?.trim();
  const visa = identity.visa?.trim();
  const portal = identity.portal ?? null;
  return (
    <div
      className="client-nav-identity flex shrink-0 flex-col gap-2 px-3 pt-3 pb-2"
      data-testid="client-nav-identity"
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar size="md">
          {identity.avatarUrl ? (
            <AvatarImage src={identity.avatarUrl} alt="" />
          ) : null}
          <AvatarFallback>{identity.initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="client-nav-name truncate text-base font-medium">
            {identity.name}
          </span>
          {email ? (
            <span className="truncate text-sm text-fg-3">{email}</span>
          ) : null}
        </div>
      </div>
      {visa || portal ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {visa ? <Tag>{visa}</Tag> : null}
          {portal ? (
            <span
              className="inline-flex"
              title={portalHint(portal) ?? undefined}
            >
              {portal === "active" ? (
                <StatusBadge tone="success" dot>
                  Portal Active
                </StatusBadge>
              ) : (
                <StatusBadge dot>Portal Off</StatusBadge>
              )}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * The client's nested sidebar: grouped sections with a collapsible Profile
 * submenu and Settings pinned to the bottom. Built from the shadcn sidebar
 * menu primitives inside a plain <nav>, so it shares the app rail's look
 * without a second SidebarProvider.
 */
export const ClientNav: FC<ClientNavProps> = ({
  activeTab,
  onSelect,
  badgeCounts,
  isProfileExpanded,
  onProfileExpandedChange,
  totals,
  identity,
  requestsTree,
  requestsQuickAction,
  isRequestsExpanded = false,
  onRequestsExpandedChange,
  className
}) => {
  const countFor = (item: NavItem) =>
    item.badge ? badgeCounts[item.badge] ?? 0 : 0;

  return (
    <nav
      aria-label="Client sections"
      className={cn(
        "client-nav flex h-full min-h-0 w-68 flex-none flex-col border-r border-line-2 bg-surface text-fg",
        className
      )}
    >
      {identity ? (
        <IdentityHeader identity={identity} />
      ) : (
        <div className="flex h-9 shrink-0 items-center px-4">
          <span className="text-base font-medium text-fg-3">Client</span>
        </div>
      )}

      {/* The groups and Settings read as one white card in Comfortable
          (styles/client-profile-cards.css); transparent in Compact. */}
      <div className="client-nav-card contents">
        <div className="client-nav-groups flex min-h-0 flex-1 flex-col overflow-y-auto">
          {NAV_GROUPS.map((group) =>
            group.kind === "items" ? (
              <SidebarGroup key={group.id}>
                {group.label ? (
                  <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                ) : null}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((entry) =>
                      entry.id === "outgoing_intakes" &&
                      requestsTree &&
                      onRequestsExpandedChange ? (
                        <RequestsNavButton
                          key={entry.id}
                          item={entry}
                          active={activeTab === entry.id}
                          count={countFor(entry)}
                          total={totals?.[entry.id]}
                          expanded={isRequestsExpanded}
                          onExpandedChange={onRequestsExpandedChange}
                          onSelect={onSelect}
                          tree={requestsTree}
                          quickAction={requestsQuickAction}
                        />
                      ) : (
                        <NavButton
                          key={entry.id}
                          item={entry}
                          active={activeTab === entry.id}
                          count={countFor(entry)}
                          total={totals?.[entry.id]}
                          onSelect={onSelect}
                        />
                      )
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ) : (
              <SidebarGroup key={group.id}>
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <Collapsible
                      open={isProfileExpanded}
                      onOpenChange={onProfileExpandedChange}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            isActive={
                              !isProfileExpanded &&
                              isProfileSubmenuTab(activeTab)
                            }
                            aria-expanded={isProfileExpanded}
                          >
                            <group.parentIcon />
                            <span>{group.parentLabel}</span>
                            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub className="mx-0 border-l-0 pr-0 pl-4">
                            {group.items.map((item) => {
                              const active = activeTab === item.id;
                              const Icon = item.icon;
                              return (
                                <SidebarMenuSubItem key={item.id}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={active}
                                  >
                                    <button
                                      type="button"
                                      aria-current={active ? "page" : undefined}
                                      onClick={() => onSelect(item.id)}
                                      className="w-full"
                                    >
                                      <Icon />
                                      <span>{item.label}</span>
                                    </button>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )
          )}
        </div>

        <div className="shrink-0 border-t border-line-1">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {NAV_FOOTER_ITEMS.map((item) => (
                  <NavButton
                    key={item.id}
                    item={item}
                    active={activeTab === item.id}
                    count={countFor(item)}
                    onSelect={onSelect}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      </div>
    </nav>
  );
};
